import productsServiceCommon, { generateProppingImageResources } from '../';
import { getProppingBounds } from '../models';
import { FILTERS, ITEMS, MISSING_PRODUCT_CATEGORIES } from '../../../constants';
import { selectRangeDataSlice } from '../../../state/rangeData/rangeDataSelectors';
import { ELVARLI_VARIANT } from '../../../components/Sheets/SelectElvarliVariantSheet/SelectElvarliVariantSheet';
import {
  selectFilterItems,
  selectProductMenuFilter,
  selectFilterHasSelectableColors,
  selectCurrentColorFilter,
} from '../../../state/productMenu/productMenuSelectors';
import store from '../../../state';
import { subFilter, subFilterObject } from '../itemsFilters';
import settings from '../../../settings/constants';
import ProductMenuSubFilter from '../../../components/ProductMenu/ProductMenuSubFilter';
import ProductMenuColorFilter from '../../../components/ProductMenu/ProductMenuColorFilter';
import { getItemPrice } from '../../../state/tac/tacHelpers';

const articleImages = [];

const LEFT = 'left';
const RIGHT = 'right';

function getArticleImage(id) {
  return articleImages.find(image => image.name === id);
}

function getDragMode(product) {
  return settings.DRAG_MODE.FLOAT;
}

function isClothesRail(id) {
  return productsServiceCommon.isType(id, ITEMS.CLOTHES_RAIL);
}

function isBracket(id) {
  return productsServiceCommon.isType(id, ITEMS.BRACKET);
}

function hasMissingBrackets(product) {
  const elvarliVariant = selectRangeDataSlice(ELVARLI_VARIANT)(
    store.getState()
  );
  if (elvarliVariant === ITEMS.SECTION_SIDE_UNITS) return false;

  // We only consider left brackets, since those are the ones connected to IOWS.
  const brackets = ['00317527_L', '00296172_L'];

  const getParts = product =>
    product?.parts ? Object.values(product.parts) : [];
  const getPartsRecursively = product => {
    const parts = getParts(product);
    const recursivelyFound = parts.reduce((alreadyFound, part) => {
      const product = productsServiceCommon.getProduct(part);
      return [
        ...alreadyFound,
        ...(product ? getPartsRecursively(product) : []),
      ];
    }, []);
    return [...parts, ...recursivelyFound];
  };

  const parts = getPartsRecursively(product);
  const bracketsNeeded = parts.filter(part => brackets.includes(part));
  const bracketsMissing = bracketsNeeded.filter(
    id => !productsServiceCommon.getProduct(id)
  );

  return !!bracketsMissing.length;
}

function getFit(item, slot) {
  let fits;
  switch (item.filter.type) {
    case ITEMS.SECTION_SIDE_UNITS:
    case ITEMS.SECTION_POSTS:
    case ITEMS.SIDE_PANEL:
      fits = productsServiceCommon.getFilteredItems(
        product => productsServiceCommon.isType(product, item.filter.type),
        {
          width: item.width,
          depth: item.depth,
          height: slot.height,
        }
      );
      break;
    case ITEMS.SHELF:
      fits = productsServiceCommon.getFilteredItems(
        productsServiceCommon.isShelf,
        {
          width: slot.width,
          depth: slot.depth,
          color: slot.color || item.filter.color,
        }
      );
      break;
    case ITEMS.DRAWER:
      fits = productsServiceCommon.getFilteredItems(
        productsServiceCommon.isDrawer,
        {
          width: slot.width,
          depth: slot.depth,
        }
      );
      break;
    case ITEMS.CLOTHES_RAIL:
      fits = productsServiceCommon.getFilteredItems(isClothesRail, {
        width: slot.width,
        depth: slot.depth,
      });
      break;
    case ITEMS.BRACKET:
      fits = productsServiceCommon.getFilteredItems(isBracket, {
        id: item.id,
      });
      break;
    default:
      fits = productsServiceCommon.getFilteredItems(
        other =>
          productsServiceCommon.isType(
            other,
            slot.filter ? slot.filter.type : item.filter.type
          ),
        {
          color: slot.color || item.filter.color,
          width: slot.width,
          depth: slot.depth,
        }
      );
      break;
  }

  if (fits?.length) {
    return Object.assign({}, fits[0]);
  }
}

/**
 * Gets a matching shelf for a specific section
 * @param {*} section
 * @returns {Object | undefined}
 */
const getFittingShelf = section => {
  const WIDTH_DIFF = 44;
  const DEPTH_DIFF = 40;
  const match = productsServiceCommon.getShelves({
    width: section.width - WIDTH_DIFF,
    depth: section.depth - DEPTH_DIFF,
  })?.[0];

  return match ? { ...match } : null;
};

/**
 * Gets a matching shelf for a specific section, the cheapest one.
 * @param {*} section
 * @returns {Object | undefined}
 */
export function getCheapestFittingShelf(section) {
  const WIDTH_DIFF = 44;
  const DEPTH_DIFF = 40;
  const match = productsServiceCommon
    .getShelves({
      width: section.width - WIDTH_DIFF,
      depth: section.depth - DEPTH_DIFF,
    })
    ?.reduce(
      (acc, curr) => {
        const itemPrice = getItemPrice(curr);
        if (itemPrice <= acc.price) {
          return {
            item: curr,
            price: itemPrice,
          };
        }
        return acc;
      },
      { item: {}, price: Infinity }
    );

  return match.price !== Infinity ? match.item : null;
}

function makeArticleImages() {
  const imageList = productsServiceCommon.getAll().map(product => {
    const { color } = product.filter;

    const sceneFileName = `${product.modelid}_${color}_0001.png`;
    const menuFileName = `${product.modelid}_${color}_t_0001.png`;

    const hasPortraitSpecificThumb = false;
    return {
      name: product.id,
      color: color,
      type: product.filter.type,
      modelid: product.modelid,
      proppingBounds: getProppingBounds(product),
      url: !productsServiceCommon.isSection(product)
        ? `${settings.IMAGE_ROOT}${sceneFileName}`
        : `${settings.IMAGE_ROOT}thumbnails/${menuFileName}`, // FIXME
      thumbnailUrl: `${settings.IMAGE_ROOT}thumbnails/${menuFileName}`,
      thumbnailUrlPortrait: hasPortraitSpecificThumb
        ? `${settings.IMAGE_ROOT}thumbnails/portrait/${menuFileName}`
        : null,
    };
  });

  articleImages.push(...generateProppingImageResources(imageList));
}

function isInsert(item) {
  return productsServiceCommon.isType(item, [
    ITEMS.SHELF,
    ITEMS.DRAWER,
    ITEMS.SHELF_DRAWER,
    ITEMS.SHELF_CLOTHES_RAIL,
    ITEMS.SHOE_SHELF,
  ]);
}

function isSection(item) {
  return productsServiceCommon.isType(item, [
    ITEMS.SECTION_SIDE_UNITS,
    ITEMS.SECTION_POSTS,
  ]);
}

export function isSectionPosts(item) {
  return productsServiceCommon.isType(item, ITEMS.SECTION_POSTS);
}

function getFittingBracket(item, side) {
  const bracketItems = [ITEMS.SHELF, ITEMS.SHELF_CLOTHES_RAIL, ITEMS.DRAWER];
  const itemShouldHaveBracket = bracketItems.some(itemType =>
    productsServiceCommon.isType(item, itemType)
  );

  let bracketId;
  if (itemShouldHaveBracket && item.depth === 360 && side === LEFT) {
    bracketId = '00317527_L';
  } else if (itemShouldHaveBracket && item.depth === 360 && side === RIGHT) {
    bracketId = '00317527_R';
  } else if (itemShouldHaveBracket && item.depth === 510 && side === LEFT) {
    bracketId = '00296172_L';
  } else if (itemShouldHaveBracket && item.depth === 510 && side === RIGHT) {
    bracketId = '00296172_R';
  } else bracketId = null;

  const bracketProduct = bracketId
    ? productsServiceCommon.getProduct(bracketId)
    : null;

  return bracketProduct;
}

function getInitialZPos(item) {
  if (isSection(item)) {
    return 5;
  }
  return 0;
}

function shouldHaveColorSelector(item) {
  if (productsServiceCommon.isType(item, ITEMS.SHELF_DRAWER)) {
    return item.items.length === Object.values(item.parts).length;
  }
  return productsServiceCommon.isType(item, [
    ITEMS.SHELF,
    ITEMS.SHELF_CLOTHES_RAIL,
  ]);
}

const getPostWidth = () => {
  const state = store.getState();
  const elvarliVariant = selectRangeDataSlice(ELVARLI_VARIANT)(state);

  return elvarliVariant
    ? settings.POST_WIDTH[elvarliVariant]
    : settings.POST_WIDTH[ITEMS.SECTION_POSTS];
};

function shouldIgnoreFloorAndCeilingCollision(item) {
  return (
    item.boundsType === 'space' &&
    isInsert(item) &&
    !item.items?.length &&
    !productsServiceCommon.isType(item, ITEMS.SHELF_CLOTHES_RAIL) // Ugly fix for a propping based item (only has item.items as propping and not actual items).
  );
}

const sectionPostFilter = state => {
  const elvarliVariant = selectRangeDataSlice(ELVARLI_VARIANT)(state);
  return ({ product }) => product.filter.type === elvarliVariant?.toLowerCase();
};

export const selectHasPostsAndSections = state => {
  const filterItems = selectFilterItems(state, FILTERS.SECTIONS).map(
    ({ filter: { type } }) => type
  );

  const hasBoth = [ITEMS.SECTION_SIDE_UNITS, ITEMS.SECTION_POSTS].reduce(
    (acc, curr) => acc && filterItems.some(id => id.includes(curr)),
    true
  );

  return hasBoth;
};

export const selectAvailableElvarliVariants = state => {
  const sections = selectFilterItems(state, FILTERS.SECTIONS).map(
    ({ filter: { type } }) => type
  );

  return [...new Set(sections)];
};

export const isntPostVariant = () =>
  !(
    selectRangeDataSlice(ELVARLI_VARIANT)(store.getState()) === 'section-posts'
  );

const sectionPostFilterObject = () => ({
  Component: () => null,
  condition: () => !!selectRangeDataSlice(ELVARLI_VARIANT)(store.getState()),
  filter: sectionPostFilter,
  appliesTo: [FILTERS.SECTIONS],
});

// SE COMMENTS INSIDE 'getFilters' BELOW
// eslint-disable-next-line no-unused-vars
const sideUnitsSubFilterObject = () => ({
  Component: ProductMenuSubFilter,
  condition: () => {
    const currentFilter = selectProductMenuFilter(store.getState());
    const elvarliVariant = selectRangeDataSlice(ELVARLI_VARIANT)(
      store.getState()
    );

    return (
      !!currentFilter.subFilter && elvarliVariant === ITEMS.SECTION_SIDE_UNITS
    );
  },
  filter: subFilter,
  appliesTo: [FILTERS.SECTIONS],
});

/**
 * Removes freestanding clothes rail
 * See {@link getFilters} for docs on filters structure.
 * @returns object
 */
const removeClothesRailFilterObject = () => ({
  Component: () => null,
  condition: () => {
    const elvarliVariant = selectRangeDataSlice(ELVARLI_VARIANT)(
      store.getState()
    );
    return elvarliVariant === ITEMS.SECTION_SIDE_UNITS;
  },
  filter:
    state =>
    ({ product: { id } }) =>
      !['40296212', '00296214'].includes(id),
  appliesTo: [FILTERS.PARTS],
});

/**
 * Removes articles for which the needed brackets are not available.
 * See {@link getFilters} for docs on filters structure.
 * @returns object
 */
const removeArticlesWithUnavailableBracketsFilterObject = () => ({
  Component: () => null,
  condition: () => {
    const elvarliVariant = selectRangeDataSlice(ELVARLI_VARIANT)(
      store.getState()
    );
    return elvarliVariant === ITEMS.SECTION_POSTS;
  },
  filter:
    state =>
    ({ product }) =>
      !hasMissingBrackets(product),
  appliesTo: [FILTERS.PARTS],
});

/**
 * Include shoeshelf and clothesrail no matter what color is has so it is always included in all color choices
 * @returns object
 */
const includeShoeshelfAndClothesrailFilterObject = () => ({
  Component: ProductMenuColorFilter,
  condition: () => selectFilterHasSelectableColors(store.getState()),
  filter: state => {
    const { colors } = selectProductMenuFilter(state);
    const currentColorFilter = selectCurrentColorFilter(state);

    const hasCurrentColorFilter =
      currentColorFilter && Object.keys(currentColorFilter).length > 0;

    const productIdIsShoeshelfOrClothesrail = productId =>
      ['10317292', '50317290', '40296212', '00296214'].includes(productId);

    return ({ product }) => {
      if (productIdIsShoeshelfOrClothesrail(product.id)) return true;
      return colors && hasCurrentColorFilter
        ? product?.filter &&
            currentColorFilter.colors?.some(
              color => product.filter.color === color
            )
        : true;
    };
  },
  appliesTo: [FILTERS.PARTS],
});

/**
 * See {@link getFilters} for docs on filters structure.
 * @type {[{filter: (function(*=): function({product: *}): boolean), condition: (function(): boolean), Component: () => JSX.Element}]}
 */
const getFilters = () => [
  sectionPostFilterObject(),
  removeClothesRailFilterObject(),
  removeArticlesWithUnavailableBracketsFilterObject(),
  /**
   * TODO: THIS SHOULD BE REINSTATED ONCE ELVARLI POST'S DEPTH CAN BE
   * SET BY IT'S INSERTS. 'sideUnitsSubFilterObject', SHOULD BE USED,
   * WHILE 'subFilterObject' SHOULD BE REMOVE AGAIN.
   */
  //sideUnitsSubFilterObject(),
  subFilterObject({ appliesTo: [FILTERS.SECTIONS] }),
  includeShoeshelfAndClothesrailFilterObject(),
];

const getMissingMandatoryProductCategories = () => {
  const sections = productsServiceCommon.getFilteredItems(item =>
    isSection(item)
  );
  return sections.length === 0 ? [MISSING_PRODUCT_CATEGORIES.SECTION] : [];
};

export default {
  articleImages,
  getArticleImage,
  getDragMode,
  hasMissingBrackets,
  getFit,
  getFittingShelf,
  getInitialZPos,
  isInsert,
  isSection,
  getFittingBracket,
  makeArticleImages,
  shouldHaveColorSelector,
  shouldIgnoreFloorAndCeilingCollision,
  getPostWidth,
  getFilters,
  getMissingMandatoryProductCategories,
};
